import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { RouteComponentProps } from "react-router-dom";
import { Box, Button, TextField, Typography } from "@mui/material";

import CONFIG from "@APP/config";
import { ICONS } from "@APP/assets";
import { API } from "@APP/services";
import { SCREEN_PATHS } from "@APP/navigation";
import { PageLayout, Message, ReusableRTPSummary } from "@APP/components";
import { useAppDispatch, useAppSelector, useThunkDispatch } from "@APP/redux";
import {
  clearReusableRtpState,
  clearRtpState,
  getReusableRtpDetails,
  loadBanks,
  setReusableRtpPayerInput,
  setReusableRtpSenderData,
} from "@APP/redux/actions";
import { PAYMENT_PROVIDER } from "@APP/types";
import { REUSABLE_RTP_ERROR_CODE } from "@APP/types/errorCodes";
import { buildReusableRtpFormSchema, formatCurrency } from "@APP/utils";

interface RouteParams {
  contactKey: string;
  payloadKey: string;
}

const SECURITY_WARNING =
  "Only ever pay requests from people or organizations you know and requests you are expecting";

const ReusableRTPLandingScreen: React.FC<RouteComponentProps<RouteParams>> = ({
  match,
  history,
}) => {
  const dispatchThunk = useThunkDispatch();
  const dispatch = useAppDispatch();

  const reusableRtpState = useAppSelector((state) => state.reusableRtp);
  const banksState = useAppSelector((state) => state.banks);

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [orgLogo, setOrgLogo] = useState<string | null>(null);
  const [submitBlocked, setSubmitBlocked] = useState(false);

  const { contactKey, payloadKey } = match.params;

  const reusableRtpData = reusableRtpState.reusableRtpData;
  const isVariableAmount = reusableRtpData
    ? parseFloat(reusableRtpData.paymentDetails.amount.amount) === 0
    : false;
  const showTip = !!(reusableRtpData?.allowTip && !isVariableAmount);

  const validationSchema = buildReusableRtpFormSchema({
    isVariableAmount,
    requireCustomerName: reusableRtpData?.requireCustomerName ?? false,
    maxAmount: reusableRtpData?.maxAmountAllowed?.toString(),
    allowTip: showTip,
  });

  const formik = useFormik({
    initialValues: {
      amount: "",
      customerName: "",
      tip: "",
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Handled in handlePayClick to keep navigation logic in one place.
    },
  });

  useEffect(() => {
    // Clear both slices so stale state from the other flow never leaks into usePaymentSource.
    dispatch(clearReusableRtpState());
    dispatch(clearRtpState());

    const load = async () => {
      if (!contactKey || !payloadKey) {
        setErrorMessage("Sorry, we were unable to locate this payment request.");
        setLoading(false);
        return;
      }

      try {
        if (CONFIG.PAYMENT_PROVIDER === PAYMENT_PROVIDER.MONEYHUB) {
          await dispatchThunk(loadBanks());
        }

        // payloadKey → /payment-request-decode (payer response data).
        // Use the thunk return value directly — same pattern as LandingScreen which avoids
        // the stale-closure issue where the selector still holds the pre-dispatch state.
        await dispatchThunk(getReusableRtpDetails(payloadKey));

        // contactKey → /sender-data (supplier/org data). Degrade gracefully on failure
        // (page still loads, logo falls back to the default icon).
        let orgId: string | null = null;
        try {
          const senderData = await API.getSenderDataForReusableRTP(contactKey);
          dispatch(setReusableRtpSenderData(senderData));
          orgId = senderData?.companyInfo?.id ?? null;
        } catch {
          // senderData stays null — company name/email/logo fall back to defaults
        }

        try {
          if (orgId) {
            const logo = await API.getOrganizationLogo(orgId);
            setOrgLogo(logo);
          } else {
            setOrgLogo(ICONS.REQUEST_ICON);
          }
        } catch {
          setOrgLogo(ICONS.REQUEST_ICON);
        }
      } catch (err: any) {
        const errorCode = err?.response?.data?.errorCode ?? err?.response?.data?.code ?? null;

        if (
          errorCode === REUSABLE_RTP_ERROR_CODE.NOT_FOUND ||
          errorCode === REUSABLE_RTP_ERROR_CODE.INVALID_PAYLOAD
        ) {
          setErrorMessage("Sorry, we were unable to locate this payment request.");
        } else if (errorCode === REUSABLE_RTP_ERROR_CODE.EXPIRED) {
          setErrorMessage("This payment link has expired and is no longer valid.");
        } else if (errorCode === REUSABLE_RTP_ERROR_CODE.MAX_USES_REACHED) {
          setErrorMessage("This payment request is no longer available.");
        } else {
          setErrorMessage(
            "It looks like there is a problem, please try again in a few moments. If the problem continues please contact the person who sent you the request.",
          );
        }
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const handlePayClick = async () => {
    const errors = await formik.validateForm();
    formik.setTouched({ amount: true, customerName: true });

    if (Object.keys(errors).length > 0) return;

    // Prevent double-submission.
    if (submitBlocked) return;
    setSubmitBlocked(true);

    const tipValue = showTip && formik.values.tip ? formik.values.tip : null;
    dispatch(
      setReusableRtpPayerInput({
        payerEnteredAmount: isVariableAmount ? formik.values.amount : null,
        payerName: formik.values.customerName || null,
        payerTipAmount: tipValue,
      }),
    );

    const lastSelectedBank = banksState.selectedBank;

    if (CONFIG.PAYMENT_PROVIDER === PAYMENT_PROVIDER.MAVERICK) {
      history.push(SCREEN_PATHS.MAVERICK_PAYMENT);
      return;
    }

    if (lastSelectedBank) {
      history.push(SCREEN_PATHS.INITIATE_PAYMENT.replace(":bankId", lastSelectedBank));
      return;
    }

    history.push(SCREEN_PATHS.BANK_SELECT);
  };

  const handleReset = () => {
    dispatch(clearReusableRtpState());
    history.push("/");
  };

  if (errorMessage) {
    return (
      <PageLayout loading={false}>
        <Box display="flex" flex={1} alignItems="center" flexDirection="column">
          <Message
            style={{ margin: "auto" }}
            type="error"
            title="Oops, something seems to have gone wrong"
            description={errorMessage}
            buttons={[{ variant: "contained", color: "secondary", onClick: handleReset }]}
          />
        </Box>
      </PageLayout>
    );
  }

  const companyName = reusableRtpState.senderData?.companyInfo?.name ?? undefined;
  const companyEmail = reusableRtpState.senderData?.companyInfo?.email ?? undefined;

  return (
    <PageLayout loading={loading}>
      <Box
        display="flex"
        flex={1}
        alignItems="center"
        flexDirection="column"
        id="reusableRtpLandingMainBox">
        {!loading && reusableRtpData && (
          <>
            {orgLogo && <img src={orgLogo} style={{ width: "6.5vh" }} alt="Payment request" />}

            <Box mt="2.5vh" mb="3vh">
              <Typography align="center" id="reusableRtpLandingTitle">
                You have been sent a payment request
              </Typography>
            </Box>

            <ReusableRTPSummary
              reusableRtpData={reusableRtpData}
              companyName={companyName}
              companyEmail={companyEmail}
            />

            {isVariableAmount && (
              <Box mt={2} width="100%" maxWidth={320} id="reusableRtpLandingAmountBox">
                <TextField
                  fullWidth
                  id="reusableRtpLandingAmountInput"
                  name="amount"
                  label="Amount (GBP)"
                  inputProps={{ inputMode: "decimal", "aria-label": "Enter payment amount" }}
                  value={formik.values.amount}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.amount && Boolean(formik.errors.amount)}
                  helperText={formik.touched.amount && formik.errors.amount}
                  variant="outlined"
                />
              </Box>
            )}

            {showTip && (
              <Box mt={2} width="100%" maxWidth={320} id="reusableRtpLandingTipBox">
                <Typography
                  variant="body2"
                  align="center"
                  gutterBottom
                  id="reusableRtpLandingTipPrompt">
                  Would you like to add a tip? Please enter the tip amount below:
                </Typography>
                <TextField
                  fullWidth
                  id="reusableRtpLandingTipInput"
                  name="tip"
                  label="Tip amount (optional)"
                  inputProps={{ inputMode: "decimal", "aria-label": "Enter tip amount" }}
                  value={formik.values.tip}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.tip && Boolean(formik.errors.tip)}
                  helperText={formik.touched.tip && formik.errors.tip}
                  variant="outlined"
                />
                {formik.values.tip && !formik.errors.tip && parseFloat(formik.values.tip) > 0 && (
                  <Box mt={1} textAlign="center" id="reusableRtpLandingTotalBox">
                    <Typography variant="body2" id="reusableRtpLandingTotalLabel">
                      Total payment amount:
                    </Typography>
                    <Typography
                      variant="h5"
                      color="textSecondary"
                      id="reusableRtpLandingTotalAmount">
                      {formatCurrency({
                        amount: (
                          parseFloat(reusableRtpData!.paymentDetails.amount.amount) +
                          parseFloat(formik.values.tip)
                        ).toFixed(2),
                        currency: reusableRtpData!.paymentDetails.amount.currency,
                      })}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            <Box mt={2} width="100%" maxWidth={320} id="reusableRtpLandingNameBox">
              <TextField
                fullWidth
                id="reusableRtpLandingCustomerNameInput"
                name="customerName"
                label={reusableRtpData.requireCustomerName ? "Your name *" : "Your name (optional)"}
                inputProps={{ "aria-label": "Enter your name" }}
                value={formik.values.customerName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.customerName && Boolean(formik.errors.customerName)}
                helperText={formik.touched.customerName && formik.errors.customerName}
                variant="outlined"
              />
            </Box>

            <Box mt={3} mb={2} maxWidth={420}>
              <Typography
                variant="caption"
                align="center"
                display="block"
                id="reusableRtpLandingSecurityWarning">
                {SECURITY_WARNING}
              </Typography>
            </Box>

            <Box minWidth="200px" maxWidth="240px" width="100%">
              <Button
                size="large"
                fullWidth
                color="primary"
                id="reusableRtpLandingPayButton"
                variant="contained"
                disabled={submitBlocked}
                onClick={handlePayClick}>
                Pay
              </Button>
            </Box>
          </>
        )}
      </Box>
    </PageLayout>
  );
};

export default ReusableRTPLandingScreen;
